import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, Pagination, Select, Skeleton, toast } from '@/components/ui';
import { ORDER_PAGE_SIZES, useOrderFiltersStore } from '@/stores/useOrderFiltersStore';
import { OrdersTable } from '../components/OrdersTable';
import { OrderItemsListTable, type OrderWithLines } from '../components/OrderItemsListTable';
import { OrderFiltersBar } from '../components/OrderFiltersBar';
import { OrderListTotals } from '../components/OrderListTotals';
import { useOrdersSummaryQuery } from '../hooks/useOrdersSummaryQuery';
import { BulkActionFooter } from '../components/BulkActionFooter';
import { OrderExportDialog } from '../components/OrderExportDialog';
import { OrderColumnSettings } from '../components/OrderColumnSettings';
import { BulkPrintDocumentModal } from '../printDocuments/BulkPrintDocumentModal';
import { OrderStatusChangeForm } from '../components/OrderStatusChangeForm';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui';
import type { PrintDocumentKind } from '../printDocuments/types';
import { useOrdersQuery } from '../hooks/useOrdersQuery';
import { selfCodeLineMatcher } from '../lib/orderSearchType';
import { useOrderActionMutation } from '../hooks/useOrderActionMutation';
import { useConfirmPurchaseMutation } from '../hooks/useConfirmPurchaseMutation';
import { canPerformAction } from '../lib/orderActionRbac';
import { canCancelPendingOrder, canConfirmPurchase } from '../lib/confirmPurchaseRbac';
import { useAuthContext } from '@/providers/AuthProvider';
import { ProductCollectionExportDialog } from '@/features/productCollectionInputs/components/ProductCollectionExportDialog';
import { EXPORT_ROLES, hasAnyRole } from '@/features/productCollectionInputs/lib/piiAccess';

/** HH:mm:ss — 목록 조회 시각. */
const formatClock = (ms: number): string => {
  const d = new Date(ms);
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const OrderListPage = () => {
  const { t } = useTranslation('orders');
  const [exportOpen, setExportOpen] = useState(false);
  // QA-455 — 고른 주문 출력물 일괄 출력(열 때 고른 주문을 고정한다).
  const [bulkPrint, setBulkPrint] = useState<{ kind: PrintDocumentKind; orderIds: string[] } | null>(null);
  // QA-457 — 고른 주문 상태 일괄 변경(열 때 고른 주문을 고정). 100건이 넘으면 100건씩 나눠 보낸다(QA-537, BE 제한).
  const [statusChangeIds, setStatusChangeIds] = useState<string[] | null>(null);
  // 상품 추가입력(개인정보 원문) 엑셀 — SUPER_ADMIN·OPS_ADMIN 만(BE 403 과 같은 기준이라 그 외엔 버튼을 숨긴다).
  const [collectionExportOpen, setCollectionExportOpen] = useState(false);
  const [orderCollectionExportOpen, setOrderCollectionExportOpen] = useState(false);
  const { currentAdmin } = useAuthContext();
  const canExportCollection = hasAnyRole(currentAdmin?.roles ?? [], EXPORT_ROLES);

  const searchType = useOrderFiltersStore((s) => s.searchType);
  const q = useOrderFiltersStore((s) => s.q);
  const ordererName = useOrderFiltersStore((s) => s.ordererName);
  const ordererPhone = useOrderFiltersStore((s) => s.ordererPhone);
  const malls = useOrderFiltersStore((s) => s.malls);
  const paymentMethods = useOrderFiltersStore((s) => s.paymentMethods);
  const displayLabelCodes = useOrderFiltersStore((s) => s.displayLabelCodes);
  const orderStatuses = useOrderFiltersStore((s) => s.orderStatuses);
  const paymentStatuses = useOrderFiltersStore((s) => s.paymentStatuses);
  const fulfillmentTypes = useOrderFiltersStore((s) => s.fulfillmentTypes);
  const fulfillmentStatuses = useOrderFiltersStore((s) => s.fulfillmentStatuses);
  const pickupStatuses = useOrderFiltersStore((s) => s.pickupStatuses);
  const dateFrom = useOrderFiltersStore((s) => s.dateFrom);
  const dateTo = useOrderFiltersStore((s) => s.dateTo);
  const amountMin = useOrderFiltersStore((s) => s.amountMin);
  const amountMax = useOrderFiltersStore((s) => s.amountMax);
  // 엑셀(일반·추가입력)이 목록과 같은 조건으로 받도록 그대로 넘긴다(ID63, QA-535).
  const listFilters = {
    searchType,
    q,
    ordererName,
    ordererPhone,
    malls,
    paymentMethods,
    displayLabelCodes,
    orderStatuses,
    paymentStatuses,
    fulfillmentTypes,
    fulfillmentStatuses,
    pickupStatuses,
    dateFrom,
    dateTo,
    amountMin,
    amountMax,
  };
  const page = useOrderFiltersStore((s) => s.page);
  const size = useOrderFiltersStore((s) => s.size);
  const sort = useOrderFiltersStore((s) => s.sort);
  const selectedIds = useOrderFiltersStore((s) => s.selectedIds);
  const setPage = useOrderFiltersStore((s) => s.setPage);
  const setSize = useOrderFiltersStore((s) => s.setSize);
  const setSort = useOrderFiltersStore((s) => s.setSort);
  const toggleSelected = useOrderFiltersStore((s) => s.toggleSelected);
  const setSelected = useOrderFiltersStore((s) => s.setSelected);
  const clearSelection = useOrderFiltersStore((s) => s.clearSelection);

  // QA-524 [주문번호별 | 상품주문번호별] — 주소 ?view=items 로 남겨 새로고침·뒤로가기에도 유지.
  const [searchParams, setSearchParams] = useSearchParams();
  const byItem = searchParams.get('view') === 'items';
  const setView = (items: boolean) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (items) next.set('view', 'items');
        else next.delete('view');
        return next;
      },
      { replace: true },
    );

  const query = useOrdersQuery({
    includeLines: byItem,
    page,
    size,
    sort,
    searchType,
    q,
    ordererName,
    ordererPhone,
    malls,
    paymentMethods,
    displayLabelCodes,
    orderStatuses,
    paymentStatuses,
    fulfillmentTypes,
    fulfillmentStatuses,
    pickupStatuses,
    dateFrom,
    dateTo,
    amountMin,
    amountMax,
  });

  // QA-541 목록과 같은 검색 조건의 전체 합계(쪽·정렬·보기 방식과 무관).
  const summaryQuery = useOrdersSummaryQuery({
    searchType,
    q,
    ordererName,
    ordererPhone,
    malls,
    paymentMethods,
    displayLabelCodes,
    orderStatuses,
    paymentStatuses,
    fulfillmentTypes,
    fulfillmentStatuses,
    pickupStatuses,
    dateFrom,
    dateTo,
    amountMin,
    amountMax,
  });

  const items = query.data?.items ?? [];
  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;
  const actionMutation = useOrderActionMutation();

  // QA-371 — 고도몰 목록 "선택한 주문을 [상태] 일괄처리" 중 BE 가 받는 단계(배송완료·구매확정).
  // 주문마다 BE 단건 API 를 부르고, 그 단계로 갈 수 없는 주문(409 — 배송중이 아님·배송완료가 아님 등)은 건너뛴다.
  const roles = currentAdmin?.roles ?? [];
  const confirmPurchaseMutation = useConfirmPurchaseMutation();
  const [bulkRunning, setBulkRunning] = useState(false);
  // QA-489 — 일괄 취소 승인·반려도 같은 흐름으로 확인창과 결과 토스트를 띄운다(취소요청 상태가 아닌 주문은 BE 409 로 건너뜀).
  const BULK_LABEL_KEYS = {
    'fulfillment-complete': ['orders.bulk.markDelivered', '일괄 배송완료'],
    'confirm-purchase': ['orders.bulk.confirmPurchase', '일괄 구매확정'],
    'cancel-approve': ['orders.bulk.cancelApprove', '일괄 취소 승인'],
    'cancel-reject': ['orders.bulk.cancelReject', '일괄 취소 반려'],
  } as const;
  const runBulkProgress = async (kind: keyof typeof BULK_LABEL_KEYS) => {
    const ids = [...selectedIds];
    const [labelKey, labelDefault] = BULK_LABEL_KEYS[kind];
    const actionLabel = t(labelKey, labelDefault);
    const prompt = t('orders.bulk.progressConfirm', {
      count: ids.length,
      action: actionLabel,
      defaultValue: '선택한 {{count}}건을 {{action}} 처리할까요? 처리할 수 없는 상태의 주문은 건너뜁니다.',
    });
    if (!window.confirm(prompt)) return;
    setBulkRunning(true);
    let done = 0;
    for (const orderId of ids) {
      const body = { reason: t(`orders.bulk.defaultReason.${kind}`, actionLabel) };
      try {
        if (kind === 'confirm-purchase') await confirmPurchaseMutation.mutateAsync({ orderId, body });
        else await actionMutation.mutateAsync({ orderId, action: kind, body });
        done++;
      } catch {
        // 상태가 맞지 않는 주문(409) 등 — 건너뛴 건수로 알린다.
      }
    }
    setBulkRunning(false);
    clearSelection();
    const message = t('orders.bulk.progressResult', {
      action: actionLabel,
      done,
      skipped: ids.length - done,
      defaultValue: '{{action}}: {{done}}건 완료, {{skipped}}건 제외',
    });
    if (done > 0) toast.success(message);
    else toast.error(message);
  };

  return (
    <PageShell
      title={t('orders.list.title')}
      actions={
        <div className="flex gap-2">
          {canExportCollection ? (
            <>
              {/* QA-535 상품·기간 기준(목록 검색과 무관) — 이름으로 구분한다. */}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setCollectionExportOpen(true)}
                data-testid="collection-export-button"
              >
                {t('orders.collectionExport.productButton', '상품별 추가입력 엑셀')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setOrderCollectionExportOpen(true)}
                data-testid="order-collection-export-button"
              >
                {t('orders.collectionExport.button', '추가입력 엑셀 다운로드')}
              </Button>
            </>
          ) : null}
          {/* QA-536 목록에 보일 칸(상품 목록 노출 항목과 같은 방식). */}
          <OrderColumnSettings />
          <Button type="button" size="sm" onClick={() => setExportOpen(true)} data-testid="order-export-button">
            {t('orders.export.button')}
          </Button>
        </div>
      }
    >
      <OrderFiltersBar />

      <div className="flex items-end justify-between gap-2 border-b" style={{ borderColor: 'var(--divider-strong)' }}>
      <div role="tablist" aria-label="목록 보기" className="flex gap-1" data-testid="order-list-view-tabs">
        {[
          { key: false, label: '주문번호별' },
          { key: true, label: '상품주문번호별' },
        ].map((tab) => (
          <button
            key={tab.label}
            type="button"
            role="tab"
            aria-selected={byItem === tab.key}
            onClick={() => setView(tab.key)}
            className="-mb-px px-4 py-2 text-[13px] font-medium"
            style={{
              borderBottom: `2px solid ${byItem === tab.key ? 'var(--accent-default)' : 'transparent'}`,
              color: byItem === tab.key ? 'var(--content-strong)' : 'var(--content-muted)',
            }}
            data-testid={`order-list-view-${tab.key ? 'items' : 'orders'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
        <div className="flex items-end gap-3">
        {/* QA-541 검색 결과 전체 합계(텍스트). */}
        <OrderListTotals summary={summaryQuery.data} />
        {/* QA-577 언제 조회한 결과인지 + 바로 다시 조회. */}
        {query.dataUpdatedAt ? (
          <span className="mb-2 inline-flex items-center gap-1 whitespace-nowrap text-[12px]" style={{ color: 'var(--content-muted)' }} data-testid="order-list-fetched-at">
            {formatClock(query.dataUpdatedAt)} 조회
            <button
              type="button"
              onClick={() => {
                void query.refetch();
                void summaryQuery.refetch();
              }}
              disabled={query.isFetching}
              className="underline disabled:no-underline disabled:opacity-50"
              data-testid="order-list-refresh"
            >
              {query.isFetching ? '조회 중…' : '새로고침'}
            </button>
          </span>
        ) : null}
        {/* QA-537 페이지당 N개 보기(고도몰과 같은 선택지) — 바꾸면 1쪽부터. */}
        <Select
          className="mb-1 w-32"
          value={String(size)}
          onChange={(e) => setSize(Number(e.target.value))}
          aria-label={t('orders.list.pageSize', '표시 개수')}
          data-testid="order-list-page-size"
        >
          {ORDER_PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {t('orders.list.pageSizeOption', { n, defaultValue: '{{n}}개 보기' })}
            </option>
          ))}
        </Select>
        </div>
      </div>

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[var(--density-default-row-h)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('orders.list.error')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('orders.list.retry')}
          </Button>
        </div>
      ) : (
        byItem ? (
          <OrderItemsListTable
            items={items as ReadonlyArray<OrderWithLines>}
            selectedIds={selectedIds}
            onToggleRow={toggleSelected}
            onToggleAll={setSelected}
            lineFilter={selfCodeLineMatcher(searchType, q)}
          />
        ) : (
          <OrdersTable
            items={items}
            sort={sort}
            onSortChange={setSort}
            selectedIds={selectedIds}
            onToggleRow={toggleSelected}
            onToggleAll={setSelected}
          />
        )
      )}

      <Pagination
        total={totalItems ?? 0}
        currentPage={page}
        pageSize={size}
        onPageChange={setPage}
        className="mt-4"
      />

      <BulkActionFooter
        selectedCount={selectedIds.length}
        disabled={actionMutation.isPending || bulkRunning}
        onCancelApprove={() => void runBulkProgress('cancel-approve')}
        onCancelReject={() => void runBulkProgress('cancel-reject')}
        {...(canPerformAction('fulfillment-complete', roles) ? { onMarkDelivered: () => void runBulkProgress('fulfillment-complete') } : {})}
        {...(canConfirmPurchase(roles) ? { onConfirmPurchase: () => void runBulkProgress('confirm-purchase') } : {})}
        onPrint={(kind) => setBulkPrint({ kind, orderIds: [...selectedIds] })}
        {...(['OPS_ADMIN', 'CS_AGENT', 'FULFILLMENT_AGENT', 'SUPER_ADMIN'].some((r) => (roles as ReadonlyArray<string>).includes(r))
          ? { onStatusChange: () => setStatusChangeIds([...selectedIds]) }
          : {})}
        onClearSelection={clearSelection}
      />

      {statusChangeIds ? (
        <Dialog open onOpenChange={(o) => (o ? undefined : setStatusChangeIds(null))}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>주문상태 일괄 변경</DialogTitle>
              <DialogDescription>
                고른 주문 {statusChangeIds.length}건의 주문 상태를 바꿉니다.{statusChangeIds.length > 100 ? ' (100건씩 나눠 처리합니다)' : ''}
              </DialogDescription>
            </DialogHeader>
            <OrderStatusChangeForm
              orderIds={statusChangeIds}
              allowPurchaseConfirm={canConfirmPurchase(roles)}
              allowCancel={canCancelPendingOrder(roles)}
              onDone={() => {
                setStatusChangeIds(null);
                clearSelection();
              }}
            />
          </DialogContent>
        </Dialog>
      ) : null}

      {bulkPrint ? (
        <BulkPrintDocumentModal orderIds={bulkPrint.orderIds} kind={bulkPrint.kind} onClose={() => setBulkPrint(null)} />
      ) : null}

      <OrderExportDialog open={exportOpen} onClose={() => setExportOpen(false)} listFilters={listFilters} />

      {canExportCollection ? (
        <>
          {/* QA-535 [추가입력 엑셀 다운로드] — 주문 엑셀을 추가입력 칸으로 연다(목록 검색 결과만). */}
          <OrderExportDialog
            open={orderCollectionExportOpen}
            onClose={() => setOrderCollectionExportOpen(false)}
            listFilters={listFilters}
            mode="collection"
          />
          <ProductCollectionExportDialog open={collectionExportOpen} onOpenChange={setCollectionExportOpen} />
        </>
      ) : null}
    </PageShell>
  );
};
